/**
 * Strips — rows the page stops on and scrolls sideways ([data-strip]).
 *
 * When a strip reaches the middle of the screen the PAGE stops and the
 * scroll drives the strip sideways; once the strip reaches its end the page
 * moves on (and the other way round on the way up). Nothing is added to the
 * page height: pinning made the section a full screen tall, with the strip
 * floating in a gap that grew with the screen.
 *
 * Wheel and keyboard are taken over while a strip is on the page (a
 * trackpad gesture can no longer be caught once it has started); touch only
 * when the gesture starts near a strip. ScrollSmoother keeps smoothing the
 * page, the strip eases on its own. Reduced motion: a swipeable strip.
 *
 * @module motion/strips
 */

import { $, $$ } from '../lib/dom.js';
import { ScrollTrigger, gsap } from '../lib/gsap.js';
import { getSmoother } from '../lib/scroll.js';

const SETTINGS = {
    speed: 1 / 1.15, // sideways px per scrolled px
    catchUp: 1, // seconds for the strip to reach its position
    tolerance: 1, // px under which the page counts as stopped on a strip
    wheelLine: 40, // px per wheel "line" (deltaMode 1, Firefox)
    arrowStep: 40,
    pageRatio: 0.875, // share of the screen for Page Down / Space
    axisLock: 8, // finger travel before a gesture picks its axis
    friction: 0.95, // touch momentum, per 16 ms frame
    minVelocity: 0.04, // px/ms under which momentum stops
    staleDelay: 80, // ms without movement: the finger rests, no momentum
};

/**
 * @typedef {object} Strip
 * @property {HTMLElement} section
 * @property {HTMLElement} track
 * @property {number} distance - Sideways travel, in px.
 * @property {number} length - Scroll it takes to travel it, in px.
 * @property {number} progress - Scroll consumed so far, in px.
 */

/** @param {Strip} strip */
function measure(strip) {
    strip.distance = Math.max(0, strip.track.scrollWidth - window.innerWidth);
    strip.length = strip.distance / SETTINGS.speed;
    strip.progress = Math.min(strip.progress, strip.length);
}

/**
 * @param {Strip} strip
 * @param {1 | -1} direction
 * @returns {boolean}
 */
function canMove(strip, direction) {
    return direction > 0 ? strip.progress < strip.length - 0.5 : strip.progress > 0.5;
}

/**
 * @param {Strip} strip
 * @param {boolean} immediate - Jump instead of easing (touch).
 */
function draw(strip, immediate) {
    const x = -strip.progress * SETTINGS.speed;

    if (immediate) {
        gsap.set(strip.track, { x, overwrite: true });
    } else {
        gsap.to(strip.track, { x, duration: SETTINGS.catchUp, ease: 'power3.out', overwrite: true });
    }
}

/**
 * Moves a strip by `delta` scrolled px, within its bounds.
 *
 * @param {Strip} strip
 * @param {number} delta
 * @param {boolean} immediate
 * @returns {number} What it took.
 */
function move(strip, delta, immediate) {
    const next = gsap.utils.clamp(0, strip.length, strip.progress + delta);
    const moved = next - strip.progress;

    if (moved !== 0) {
        strip.progress = next;
        draw(strip, immediate);
    }

    return moved;
}

/**
 * Scroll position at which the section sits in the middle of the screen
 * (top-aligned when it is taller than the screen).
 *
 * @param {Strip} strip
 * @returns {number}
 */
function lockOf(strip) {
    const { section } = strip;
    const smoother = getSmoother();
    const gap = Math.max(0, (window.innerHeight - section.offsetHeight) / 2);
    const top = smoother ? smoother.offset(section, 'top top') : section.getBoundingClientRect().top + window.scrollY;

    return gsap.utils.clamp(0, ScrollTrigger.maxScroll(window), top - gap);
}

/**
 * Takes over the wheel, the keyboard and touch gestures to share the scroll
 * between the page and the strips.
 *
 * @param {Strip[]} strips
 * @returns {{ destroy: () => void }}
 */
function createScrollDriver(strips) {
    let gesture = null;
    let momentumFrame = 0;
    let lastSetY = null;
    let restorationHeld = false;
    let pageTween = null;
    let pageTarget = 0;

    // window.scrollY is rounded to the device pixel: small trackpad deltas
    // would get lost, so keep our own fractional position as long as nobody
    // else has moved the page.
    const pageY = () => {
        const y = window.scrollY;
        return lastSetY !== null && Math.abs(y - lastSetY) < 1 ? lastSetY : y;
    };

    // After a reload (or Back) the browser puts the page back where it was
    // until loading ends, unless the user scrolled in the meantime — and our
    // scripted scrolls do not count: the page would jump back (often to the
    // top) right after the load event. Restoration is held from our first
    // scroll during loading, then given back.
    const holdScrollRestoration = () => {
        if (restorationHeld || document.readyState === 'complete' || !('scrollRestoration' in window.history)) {
            return;
        }

        const previous = window.history.scrollRestoration;

        const release = () => {
            window.removeEventListener('load', onLoad);
            window.removeEventListener('pagehide', release);
            window.history.scrollRestoration = previous;
            restorationHeld = false;
        };

        // Not inside the load handler: the browser restores right after it.
        const onLoad = () => window.setTimeout(release, 0);

        restorationHeld = true;
        window.history.scrollRestoration = 'manual';
        window.addEventListener('load', onLoad);
        window.addEventListener('pagehide', release);
    };

    // Where the page is heading: the end of an eased move still running.
    const pageAim = () => (pageTween ? pageTarget : pageY());

    const stopPageTween = () => {
        pageTween?.kill();
        pageTween = null;
    };

    // ScrollSmoother eases the page on its own. Without it (touch-only
    // screens, DevTools device mode with a mouse, a tablet keyboard) the
    // wheel and keys would jump by whole notches, so ease them here.
    const setPageY = (y, immediate) => {
        holdScrollRestoration();

        if (!immediate && !getSmoother()) {
            pageTarget = y;
            pageTween = gsap.to(window, {
                scrollTo: { y, autoKill: true, onAutoKill: () => (pageTween = null) },
                duration: 0.5,
                ease: 'power3.out',
                overwrite: true,
                onUpdate: () => (lastSetY = window.scrollY),
                onComplete: () => (pageTween = null),
            });
            return;
        }

        stopPageTween();
        lastSetY = y;
        window.scrollTo(0, y);
    };

    // Menu open (smoother paused) or loader still up: the page is locked.
    const pageLocked = () => {
        if (document.body.classList.contains('is-loading')) {
            return true;
        }

        const smoother = getSmoother();
        return smoother ? smoother.paused() : document.body.style.overflow === 'hidden';
    };

    // The next strip that stops the page between `from` and `to`
    // (inclusive), among those that can still move that way.
    const nextStop = (from, to) => {
        const direction = Math.sign(to - from);
        let best = null;

        if (!direction) {
            return null;
        }

        strips.forEach((strip) => {
            if (!canMove(strip, direction)) {
                return;
            }

            const lock = lockOf(strip);
            const ahead =
                direction > 0
                    ? lock >= from - SETTINGS.tolerance && lock <= to
                    : lock <= from + SETTINGS.tolerance && lock >= to;

            if (ahead && (!best || Math.abs(lock - from) < Math.abs(best.lock - from))) {
                best = { strip, lock };
            }
        });

        return best;
    };

    // Splits a vertical move between the page and the strips: the page goes
    // as far as the next strip that can still move, the strip takes over,
    // then the page carries on with what is left. Returns the distance
    // actually covered.
    const consume = (delta, immediate) => {
        let position = pageAim();
        let remaining = delta;
        let consumed = 0;

        for (let guard = 0; guard <= strips.length && Math.abs(remaining) > 0.01; guard++) {
            const stop = nextStop(position, position + remaining);

            if (!stop) {
                const target = gsap.utils.clamp(0, ScrollTrigger.maxScroll(window), position + remaining);
                consumed += Math.abs(target - position);
                position = target;
                break;
            }

            consumed += Math.abs(stop.lock - position);
            remaining -= stop.lock - position;
            position = stop.lock;

            const moved = move(stop.strip, remaining, immediate);
            consumed += Math.abs(moved);
            remaining -= moved;
        }

        if (position !== pageAim()) {
            setPageY(position, immediate);
        }

        return consumed;
    };

    const stripAt = (target) =>
        target instanceof Node ? (strips.find((strip) => strip.track.parentNode.contains(target)) ?? null) : null;

    // Does the scroll belong to a scrollable element under the pointer?
    const scrollsInside = (target, delta) => {
        for (
            let element = target instanceof Element ? target : null;
            element && element !== document.body;
            element = element.parentElement
        ) {
            const scrollable =
                element.scrollHeight > element.clientHeight &&
                /(auto|scroll)/.test(getComputedStyle(element).overflowY);

            if (
                scrollable &&
                (delta > 0
                    ? element.scrollTop + element.clientHeight < element.scrollHeight - 1
                    : element.scrollTop > 0)
            ) {
                return true;
            }
        }

        return false;
    };

    const stripNearby = () => strips.some((strip) => Math.abs(lockOf(strip) - window.scrollY) <= window.innerHeight);

    const stopMomentum = () => {
        if (momentumFrame) {
            window.cancelAnimationFrame(momentumFrame);
        }
        momentumFrame = 0;
    };

    // Carries the finger's move on after release.
    const startMomentum = (initialVelocity, apply) => {
        let velocity = initialVelocity;
        let previous = null;

        const step = (time) => {
            const frame = previous === null ? 16 : Math.min(32, time - previous);
            previous = time;

            const moved = apply(velocity * frame);
            velocity *= SETTINGS.friction ** (frame / 16);

            if (moved < 0.1 || Math.abs(velocity) < SETTINGS.minVelocity) {
                momentumFrame = 0;
                return;
            }

            momentumFrame = window.requestAnimationFrame(step);
        };

        momentumFrame = window.requestAnimationFrame(step);
    };

    const onWheel = (event) => {
        if (event.defaultPrevented || event.ctrlKey || pageLocked()) {
            return;
        }

        const scale = event.deltaMode === 1 ? SETTINGS.wheelLine : event.deltaMode === 2 ? window.innerHeight : 1;
        const dx = event.deltaX * scale;
        const dy = event.deltaY * scale;

        // A sideways gesture (trackpad, Shift + wheel) over a strip moves it directly.
        if (Math.abs(dx) > Math.abs(dy)) {
            const strip = stripAt(event.target);

            if (strip && canMove(strip, dx > 0 ? 1 : -1)) {
                event.preventDefault();
                stopMomentum();
                move(strip, dx / SETTINGS.speed, false);
            }

            return;
        }

        if (!dy || scrollsInside(event.target, dy)) {
            return;
        }

        event.preventDefault();
        stopMomentum();
        consume(dy, false);
    };

    const keyDelta = (event) => {
        const page = window.innerHeight * SETTINGS.pageRatio;

        switch (event.key) {
            case 'ArrowDown':
                return SETTINGS.arrowStep;
            case 'ArrowUp':
                return -SETTINGS.arrowStep;
            case 'PageDown':
                return page;
            case 'PageUp':
                return -page;
            case ' ':
                return event.shiftKey ? -page : page;
            default:
                return 0;
        }
    };

    const onKeyDown = (event) => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || pageLocked()) {
            return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const delta = keyDelta(event);

        if (!delta || target?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) {
            return;
        }

        // Space on a link or a button activates it, it does not scroll.
        if (event.key === ' ' && target?.closest('a[href], button, summary, [role="button"]')) {
            return;
        }

        if (scrollsInside(target, delta)) {
            return;
        }

        event.preventDefault();
        stopMomentum();
        consume(delta, false);
    };

    const onTouchStart = (event) => {
        stopMomentum();
        stopPageTween();

        if (event.touches.length !== 1 || pageLocked()) {
            gesture = null;
            return;
        }

        const [touch] = event.touches;

        gesture = {
            apply: null,
            axis: null,
            lastTime: event.timeStamp,
            lastX: touch.clientX,
            lastY: touch.clientY,
            owned: null,
            startX: touch.clientX,
            startY: touch.clientY,
            strip: stripAt(event.target),
            velocity: 0,
        };
    };

    const onTouchMove = (event) => {
        if (!gesture || event.touches.length !== 1) {
            return;
        }

        // Decided on the first move, while it can still be cancelled: after
        // that the browser is already scrolling the page.
        if (gesture.owned === null) {
            gesture.owned = event.cancelable && (gesture.strip !== null || stripNearby());
        }

        if (!gesture.owned) {
            return;
        }

        event.preventDefault();

        const [touch] = event.touches;
        const current = gesture;

        if (!current.axis) {
            const totalX = Math.abs(touch.clientX - current.startX);
            const totalY = Math.abs(touch.clientY - current.startY);

            if (Math.max(totalX, totalY) < SETTINGS.axisLock) {
                return;
            }

            current.axis = totalX > totalY ? 'x' : 'y';
            current.apply =
                current.axis === 'y'
                    ? (delta) => consume(delta, true)
                    : (delta) => (current.strip ? Math.abs(move(current.strip, delta / SETTINGS.speed, true)) : 0);
        }

        // Finger up or left = forward (page down, strip towards its end).
        const delta = current.axis === 'y' ? current.lastY - touch.clientY : current.lastX - touch.clientX;
        const elapsed = event.timeStamp - current.lastTime;

        current.lastX = touch.clientX;
        current.lastY = touch.clientY;
        current.lastTime = event.timeStamp;

        if (elapsed > 0) {
            current.velocity = delta / elapsed;
        }

        current.apply(delta);
    };

    const onTouchEnd = (event) => {
        const ended = gesture;
        gesture = null;

        if (!ended?.owned || !ended.axis || event.type === 'touchcancel') {
            return;
        }

        if (
            event.timeStamp - ended.lastTime < SETTINGS.staleDelay &&
            Math.abs(ended.velocity) >= SETTINGS.minVelocity
        ) {
            startMomentum(ended.velocity, ended.apply);
        }
    };

    // Layout changed (resize, fonts, images): re-measure. A strip only moves
    // if it now sits out of bounds — snapping it would cut its easing short
    // and make it jump.
    const onRefresh = () => {
        strips.forEach((strip) => {
            const before = strip.progress;
            measure(strip);

            if (strip.progress !== before) {
                draw(strip, false);
            }
        });
    };

    // passive: false, or preventDefault() is ignored.
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    ScrollTrigger.addEventListener('refresh', onRefresh);

    return {
        destroy() {
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchend', onTouchEnd);
            window.removeEventListener('touchcancel', onTouchEnd);
            window.removeEventListener('keydown', onKeyDown);
            ScrollTrigger.removeEventListener('refresh', onRefresh);
            stopMomentum();
            stopPageTween();
        },
    };
}

export function animateStrips() {
    const sections = $$('[data-strip]');

    if (!sections.length) {
        return;
    }

    // Mobile browsers resize the viewport when the address bar collapses;
    // without this the scroll effects re-measure mid-scroll and jump.
    ScrollTrigger.config({ ignoreMobileResize: true });

    const media = gsap.matchMedia();

    // Driven by the scroll at every width, touch included.
    media.add('(prefers-reduced-motion: no-preference)', () => {
        /** @type {Strip[]} */
        const strips = [];

        sections.forEach((section) => {
            const track = $('[data-strip-track]', section);

            if (!track) {
                return;
            }

            const strip = { section, track, distance: 0, length: 0, progress: 0 };
            measure(strip);

            // Nothing to travel: a plain row, and no "scroll sideways" hint
            // (it would be a lie).
            if (strip.distance <= 0) {
                section.classList.add('strip--static', 'strip--no-hint');
                return;
            }

            section.classList.add('strip--driven');
            strips.push(strip);

            // The items enter with a small stagger the first time.
            gsap.from(track.children, {
                opacity: 0,
                y: 32,
                duration: 0.9,
                stagger: 0.06,
                scrollTrigger: { trigger: section, start: 'top 70%', once: true },
            });
        });

        const driver = strips.length ? createScrollDriver(strips) : null;

        return () => {
            driver?.destroy();
            $$('[data-strip-track]').forEach((track) => gsap.set(track, { x: 0 }));
            sections.forEach((section) => section.classList.remove('strip--static', 'strip--no-hint', 'strip--driven'));
        };
    });

    // Reduced motion: a strip you slide by hand.
    media.add('(prefers-reduced-motion: reduce)', () => {
        sections.forEach((section) => section.classList.add('strip--static'));

        return () => sections.forEach((section) => section.classList.remove('strip--static'));
    });
}
